/**
 * Lesson content for the Learn mode.
 *
 * Each topic has a short, authored mini-lesson designed to be read in a few
 * minutes before practicing: a concept intro, a "key idea" recognition cue, an
 * ordered method, common pitfalls, optional vocabulary, and one conceptual
 * quick check. This is the "teach me first" companion to Practice's "let me
 * try". Worked examples are NOT authored here - the Learn screen pulls a live,
 * engine-verified example from the topic's generator (for topics that have
 * one), so the numbers in the example are always correct by construction.
 *
 * Content is aligned to the 2025 South Carolina Grade 6 standards. Text fields
 * are plain strings; short inline math may use KaTeX delimited by $...$ which
 * the Learn screen renders inline.
 */

import type { TopicId } from "./curriculum";

/** A single conceptual multiple-choice check shown at the end of a lesson. */
export interface QuickCheck {
  readonly prompt: string;
  readonly choices: readonly string[];
  readonly correctIndex: number;
  readonly explanation: string;
}

export interface Lesson {
  readonly topicId: TopicId;
  readonly intro: string;
  readonly keyIdea?: string;
  readonly methodSteps: readonly string[];
  readonly pitfalls: readonly string[];
  readonly vocabulary?: readonly { readonly term: string; readonly definition: string }[];
  readonly quickCheck?: QuickCheck;
  readonly quickChecks?: readonly QuickCheck[];
}

/** Resolve a lesson's quick checks to a single list, preferring the multi form. */
export function lessonQuickChecks(lesson: Lesson): readonly QuickCheck[] {
  if (lesson.quickChecks && lesson.quickChecks.length > 0) {
    return lesson.quickChecks;
  }
  return lesson.quickCheck ? [lesson.quickCheck] : [];
}

export const LESSONS: Record<TopicId, Lesson> = {
  // --- Unit 1: Numerical Reasoning --------------------------------------
  "equivalent-forms": {
    topicId: "equivalent-forms",
    intro:
      "A fraction, a decimal, and a percent can all name the same amount. $\\tfrac{1}{2}$, $0.5$, and $50\\%$ are three ways to write one half. Being able to switch between forms lets you compare numbers and pick whichever form is easiest for a problem.",
    keyIdea:
      "Fractions, decimals, and percents are three costumes for the same number. Divide to go fraction to decimal; multiply or divide by 100 to move between decimal and percent.",
    methodSteps: [
      "Fraction to decimal: divide the numerator by the denominator.",
      "Decimal to percent: multiply by 100 and add a percent sign.",
      "Percent to decimal: drop the percent sign and divide by 100.",
      "Decimal to fraction: write the digits over the matching place value (tenths, hundredths), then reduce.",
    ],
    pitfalls: [
      "Moving the decimal the wrong direction between decimals and percents.",
      "Forgetting to reduce a fraction to lowest terms.",
    ],
    vocabulary: [
      { term: "Percent", definition: "A ratio out of 100; $37\\%$ means $\\tfrac{37}{100}$." },
    ],
    quickCheck: {
      prompt: "Which decimal equals $\\tfrac{3}{4}$?",
      choices: ["$0.34$", "$0.75$", "$3.4$", "$0.43$"],
      correctIndex: 1,
      explanation: "Divide $3 \\div 4 = 0.75$.",
    },
  },
  "compare-rationals": {
    topicId: "compare-rationals",
    intro:
      "To compare two positive rational numbers, put them in the same form or over a common denominator so you are comparing like with like. Then the larger numerator (over the same denominator) or the larger decimal wins.",
    keyIdea:
      "You can only compare fairly when the numbers are in the same form. Convert to decimals, or use a common denominator, then compare.",
    methodSteps: [
      "Choose one form: turn both into decimals, or rewrite both fractions with a common denominator.",
      "Compare place by place (decimals) or numerator by numerator (common denominator).",
      "Use $<$, $>$, or $=$ to state the comparison.",
    ],
    pitfalls: [
      "Assuming a fraction with a bigger denominator is larger - it is often smaller.",
      "Comparing numerators when the denominators are different.",
    ],
    quickCheck: {
      prompt: "Which is larger, $\\tfrac{2}{3}$ or $\\tfrac{3}{5}$?",
      choices: ["$\\tfrac{2}{3}$", "$\\tfrac{3}{5}$", "They are equal", "Cannot tell"],
      correctIndex: 0,
      explanation: "Common denominator 15: $\\tfrac{10}{15}$ vs $\\tfrac{9}{15}$, so $\\tfrac{2}{3}$ is larger.",
    },
  },
  "order-rationals": {
    topicId: "order-rationals",
    intro:
      "Ordering several positive rational numbers means arranging them from least to greatest (or greatest to least). The trick is the same as comparing two: get everything into one comparable form first.",
    keyIdea:
      "Convert every number to the same form, then sort. Decimals are usually the fastest common form for a mixed list.",
    methodSteps: [
      "Convert each number to a decimal (or use a common denominator for all of them).",
      "Line the values up and sort from least to greatest.",
      "Rewrite the sorted list in the original forms if the problem asks.",
    ],
    pitfalls: [
      "Sorting by numerator or by denominator instead of by actual value.",
      "Losing track of which converted value came from which original number.",
    ],
    quickCheck: {
      prompt: "Least to greatest: $0.5$, $\\tfrac{1}{4}$, $\\tfrac{3}{5}$?",
      choices: [
        "$\\tfrac{1}{4}, 0.5, \\tfrac{3}{5}$",
        "$0.5, \\tfrac{1}{4}, \\tfrac{3}{5}$",
        "$\\tfrac{3}{5}, 0.5, \\tfrac{1}{4}$",
        "$\\tfrac{1}{4}, \\tfrac{3}{5}, 0.5$",
      ],
      correctIndex: 0,
      explanation: "As decimals: $0.25, 0.5, 0.6$, so $\\tfrac{1}{4} < 0.5 < \\tfrac{3}{5}$.",
    },
  },
  "understand-integers": {
    topicId: "understand-integers",
    intro:
      "Integers are the whole numbers together with their negatives and zero: $\\ldots -2, -1, 0, 1, 2 \\ldots$. Negative numbers describe things below or less than a reference point - temperatures below zero, money owed, depth below sea level. Zero is the dividing line between positive and negative.",
    keyIdea:
      "Integers extend the number line in both directions from zero. A negative sign means 'the opposite side of zero.'",
    methodSteps: [
      "Place zero as the reference point in the middle of the number line.",
      "Positive integers go to the right, negative integers to the left.",
      "Use a negative sign to represent a value below or less than the reference.",
    ],
    pitfalls: [
      "Thinking a 'bigger' negative like $-8$ is greater than $-3$ - it is actually less.",
      "Forgetting zero is neither positive nor negative.",
    ],
    vocabulary: [
      { term: "Integer", definition: "A whole number or its opposite, including zero." },
    ],
    quickCheck: {
      prompt: "Which situation is best represented by $-15$?",
      choices: ["15 feet above ground", "A debt of $15", "15 apples", "A gain of 15 points"],
      correctIndex: 1,
      explanation: "A debt is less than zero, so it is represented by a negative integer.",
    },
  },
  opposites: {
    topicId: "opposites",
    intro:
      "The opposite of a number is the same distance from zero but on the other side. The opposite of $5$ is $-5$, and the opposite of $-3$ is $3$. Zero is its own opposite. Opposites always add to zero.",
    keyIdea:
      "Opposites are mirror images across zero. Taking the opposite flips the sign.",
    methodSteps: [
      "Find the number's distance from zero.",
      "Place a point the same distance on the other side of zero.",
      "The opposite has the same size but the flipped sign.",
    ],
    pitfalls: [
      "Thinking the opposite of a negative is still negative - the opposite of $-4$ is $+4$.",
      "Confusing 'opposite' with 'reciprocal.'",
    ],
    quickCheck: {
      prompt: "What is the opposite of $-7$?",
      choices: ["$-7$", "$7$", "$0$", "$\\tfrac{1}{7}$"],
      correctIndex: 1,
      explanation: "The opposite flips the sign, so the opposite of $-7$ is $7$.",
    },
  },
  "absolute-value": {
    topicId: "absolute-value",
    intro:
      "Absolute value is a number's distance from zero on the number line, so it is never negative. Written with bars, $|{-6}| = 6$ and $|6| = 6$ - both are 6 units from zero. Distance does not care about direction, only how far.",
    keyIdea:
      "Absolute value = distance from zero. The bars strip the sign, so the result is always zero or positive.",
    methodSteps: [
      "Look at the number inside the bars.",
      "Measure its distance from zero, ignoring the sign.",
      "Report that distance - always zero or positive.",
    ],
    pitfalls: [
      "Writing a negative answer for an absolute value.",
      "Thinking $|{-3}|$ and $-3$ are the same.",
    ],
    vocabulary: [
      { term: "Absolute value", definition: "A number's distance from zero; always zero or positive." },
    ],
    quickCheck: {
      prompt: "What is $|{-9}|$?",
      choices: ["$-9$", "$9$", "$0$", "$-1$"],
      correctIndex: 1,
      explanation: "$-9$ is 9 units from zero, so $|{-9}| = 9$.",
    },
  },
  "compare-order-integers": {
    topicId: "compare-order-integers",
    intro:
      "On the number line, numbers get larger as you move right and smaller as you move left. That means every positive number is greater than every negative number, and among negatives, the one closer to zero is larger. So $-2 > -8$.",
    keyIdea:
      "Further right on the number line means greater. For negatives, closer to zero is larger.",
    methodSteps: [
      "Picture the numbers on a number line.",
      "The number farther to the right is greater.",
      "Order a list by reading left to right.",
    ],
    pitfalls: [
      "Treating $-8$ as greater than $-2$ because 8 is bigger than 2.",
      "Forgetting that any positive beats any negative.",
    ],
    quickCheck: {
      prompt: "Which statement is true?",
      choices: ["$-8 > -2$", "$-2 > -8$", "$-8 = -2$", "$0 < -2$"],
      correctIndex: 1,
      explanation: "$-2$ is closer to zero (farther right), so $-2 > -8$.",
    },
  },

  // --- Unit 2: Patterns, Algebra & Functional Reasoning -----------------
  "integer-operations": {
    topicId: "integer-operations",
    intro:
      "Adding, subtracting, multiplying, and dividing integers all follow sign rules. For adding, same signs combine and keep the sign, while different signs subtract and take the sign of the larger size. Subtracting is adding the opposite. For multiplying and dividing, same signs give a positive result and different signs give a negative result.",
    keyIdea:
      "Handle the sign first, then the arithmetic. Subtracting means adding the opposite; a product or quotient is negative only when the two signs differ.",
    methodSteps: [
      "Adding: same signs, add and keep the sign; different signs, subtract and take the sign of the larger absolute value.",
      "Subtracting: rewrite as adding the opposite, then use the addition rule.",
      "Multiplying/dividing: work with the absolute values, then apply the sign rule - same signs positive, different signs negative.",
      "Check that your answer's sign makes sense.",
    ],
    pitfalls: [
      "Forgetting that subtracting a negative is the same as adding.",
      "Getting the sign of a product backward when only one factor is negative.",
    ],
    vocabulary: [
      { term: "Absolute value", definition: "A number's distance from zero; used to combine sizes before applying the sign." },
    ],
    quickChecks: [
      {
        prompt: "What is $-6 + 10$?",
        choices: ["$-16$", "$4$", "$-4$", "$16$"],
        correctIndex: 1,
        explanation: "Different signs: subtract $10 - 6 = 4$ and keep the sign of the larger size (positive), so $4$.",
      },
      {
        prompt: "What is $-4 \\times (-3)$?",
        choices: ["$-12$", "$12$", "$-7$", "$7$"],
        correctIndex: 1,
        explanation: "Same signs give a positive product: $4 \\times 3 = 12$.",
      },
    ],
  },
  "fraction-operations": {
    topicId: "fraction-operations",
    intro:
      "Fraction operations split into two styles. Adding and subtracting need a common denominator so the pieces are the same size; then you combine the numerators. Multiplying just multiplies tops and bottoms. Dividing is multiplying by the reciprocal (the fraction flipped). Always reduce the final answer.",
    keyIdea:
      "Add/subtract need a common denominator; multiply/divide do not. To divide, multiply by the reciprocal.",
    methodSteps: [
      "Add/subtract: rewrite over a common denominator, combine the numerators, then reduce.",
      "Multiply: multiply the numerators, multiply the denominators, then reduce.",
      "Divide: flip the second fraction to its reciprocal and multiply.",
      "Reduce the final answer to lowest terms.",
    ],
    pitfalls: [
      "Adding denominators when adding fractions - only the numerators combine over the common denominator.",
      "Forgetting to reduce, or forgetting to flip when dividing.",
    ],
    vocabulary: [
      { term: "Reciprocal", definition: "A fraction flipped over; $\\tfrac{a}{b}$ becomes $\\tfrac{b}{a}$." },
      { term: "Common denominator", definition: "A shared bottom number so fractions describe same-size pieces." },
    ],
    quickCheck: {
      prompt: "What is $\\tfrac{1}{2} \\div \\tfrac{1}{4}$?",
      choices: ["$\\tfrac{1}{8}$", "$2$", "$\\tfrac{1}{2}$", "$\\tfrac{2}{6}$"],
      correctIndex: 1,
      explanation: "Multiply by the reciprocal: $\\tfrac{1}{2} \\times \\tfrac{4}{1} = \\tfrac{4}{2} = 2$.",
    },
  },
  "decimal-operations": {
    topicId: "decimal-operations",
    intro:
      "Decimal arithmetic is about keeping place values lined up. To add or subtract, stack the numbers so the decimal points align. To multiply, ignore the points, multiply as whole numbers, then place the point using the total number of decimal places. To divide, shift the point to make the divisor whole first.",
    keyIdea:
      "Line up decimal points to add or subtract. For multiplication, count total decimal places; for division, make the divisor a whole number first.",
    methodSteps: [
      "Add/subtract: align the decimal points and combine, keeping the point in place.",
      "Multiply: multiply as whole numbers, then count the decimal places in both factors and place the point that many spots from the right.",
      "Divide: shift the decimal in the divisor to make it whole, shift the dividend the same amount, then divide.",
      "Check that the size of your answer is reasonable.",
    ],
    pitfalls: [
      "Not aligning the decimal points when adding or subtracting.",
      "Miscounting decimal places when multiplying.",
    ],
    quickCheck: {
      prompt: "What is $0.2 \\times 0.3$?",
      choices: ["$0.6$", "$0.06$", "$6$", "$0.5$"],
      correctIndex: 1,
      explanation: "$2 \\times 3 = 6$, and there are two decimal places total, so $0.06$.",
    },
  },
  "multiplicative-inverse": {
    topicId: "multiplicative-inverse",
    intro:
      "The multiplicative inverse of a number is its reciprocal - the value you multiply by to get 1. The reciprocal of $\\tfrac{2}{3}$ is $\\tfrac{3}{2}$, because $\\tfrac{2}{3} \\times \\tfrac{3}{2} = 1$. To find a reciprocal, just flip the fraction. A whole number like 4 is $\\tfrac{4}{1}$, so its reciprocal is $\\tfrac{1}{4}$.",
    keyIdea:
      "A number times its reciprocal equals 1. Flip the fraction to find the reciprocal.",
    methodSteps: [
      "Write the number as a fraction (a whole number $n$ is $\\tfrac{n}{1}$).",
      "Swap the numerator and denominator.",
      "Check: the number times its reciprocal should equal 1.",
    ],
    pitfalls: [
      "Confusing reciprocal (flip) with opposite (change sign).",
      "Forgetting zero has no reciprocal - you cannot divide by zero.",
    ],
    vocabulary: [
      { term: "Reciprocal", definition: "The number you multiply by to get 1; the fraction flipped." },
    ],
    quickCheck: {
      prompt: "What is the reciprocal of $5$?",
      choices: ["$-5$", "$\\tfrac{1}{5}$", "$5$", "$\\tfrac{5}{1}$"],
      correctIndex: 1,
      explanation: "$5 = \\tfrac{5}{1}$, so flipping gives $\\tfrac{1}{5}$, and $5 \\times \\tfrac{1}{5} = 1$.",
    },
  },
  "divide-fractions": {
    topicId: "divide-fractions",
    intro:
      "To divide by a fraction, multiply by its reciprocal. So $\\tfrac{3}{4} \\div \\tfrac{2}{5}$ becomes $\\tfrac{3}{4} \\times \\tfrac{5}{2}$. This works because dividing asks 'how many of the second fit into the first,' and multiplying by the reciprocal answers exactly that.",
    keyIdea:
      "Keep the first fraction, flip the second to its reciprocal, and multiply.",
    methodSteps: [
      "Leave the first fraction as it is.",
      "Replace the divisor with its reciprocal (flip it).",
      "Change the division to multiplication and multiply across.",
      "Reduce the result.",
    ],
    pitfalls: [
      "Flipping the wrong fraction - only the divisor (second one) gets flipped.",
      "Forgetting to reduce the answer.",
    ],
    quickCheck: {
      prompt: "$\\tfrac{3}{4} \\div \\tfrac{2}{5}$ is the same as which product?",
      choices: [
        "$\\tfrac{4}{3} \\times \\tfrac{2}{5}$",
        "$\\tfrac{3}{4} \\times \\tfrac{5}{2}$",
        "$\\tfrac{3}{4} \\times \\tfrac{2}{5}$",
        "$\\tfrac{4}{3} \\times \\tfrac{5}{2}$",
      ],
      correctIndex: 1,
      explanation: "Flip the divisor $\\tfrac{2}{5}$ to $\\tfrac{5}{2}$ and multiply.",
    },
  },
  "additive-inverse": {
    topicId: "additive-inverse",
    intro:
      "The additive inverse of a number is its opposite - the value you add to get zero. The additive inverse of $7$ is $-7$, since $7 + (-7) = 0$. Adding a number and its opposite always lands you back at zero, which is why 'adding the opposite' is how we handle subtraction.",
    keyIdea:
      "A number plus its additive inverse equals 0. The additive inverse just flips the sign.",
    methodSteps: [
      "Take the number.",
      "Flip its sign to get the opposite.",
      "Check: the two should add to zero.",
    ],
    pitfalls: [
      "Confusing additive inverse (adds to 0) with multiplicative inverse (multiplies to 1).",
      "Forgetting that the additive inverse of a negative is positive.",
    ],
    quickCheck: {
      prompt: "What is the additive inverse of $-12$?",
      choices: ["$12$", "$-12$", "$\\tfrac{1}{12}$", "$0$"],
      correctIndex: 0,
      explanation: "$-12 + 12 = 0$, so the additive inverse is $12$.",
    },
  },
  "parts-expression": {
    topicId: "parts-expression",
    intro:
      "An algebraic expression is built from terms, and each term has parts. In $3x + 5$, the terms are $3x$ and $5$. The $3$ is the coefficient (the number multiplying the variable), $x$ is the variable, and $5$ is the constant (a term with no variable). Naming these parts helps you talk about and simplify expressions.",
    keyIdea:
      "Terms are separated by + or -. A coefficient multiplies a variable; a constant stands alone.",
    methodSteps: [
      "Split the expression into terms at each + or - sign.",
      "In each term, the number multiplying the variable is the coefficient.",
      "A term with only a number and no variable is a constant.",
      "Count the terms to describe the expression's size.",
    ],
    pitfalls: [
      "Miscounting terms - a subtraction still separates two terms.",
      "Calling a constant a coefficient, or vice versa.",
    ],
    vocabulary: [
      { term: "Term", definition: "A part of an expression separated by + or -." },
      { term: "Coefficient", definition: "The number multiplying a variable, like the 3 in $3x$." },
      { term: "Constant", definition: "A term that is just a number, with no variable." },
    ],
    quickCheck: {
      prompt: "In $4x + 7$, what is the coefficient?",
      choices: ["$7$", "$4$", "$x$", "$11$"],
      correctIndex: 1,
      explanation: "The coefficient is the number multiplying the variable, so $4$.",
    },
  },
  "evaluate-expressions": {
    topicId: "evaluate-expressions",
    intro:
      "Evaluating an expression means substituting a value for the variable and simplifying. For $2x + 5$ when $x = 3$, replace $x$ with $3$ to get $2(3) + 5 = 11$. Order of operations matters: multiply before you add.",
    keyIdea:
      "Substitute the given value for the variable, then simplify using order of operations.",
    methodSteps: [
      "Replace every variable with its given value, using parentheses.",
      "Simplify inside parentheses first if there are any.",
      "Multiply and divide before adding and subtracting.",
      "Finish the addition and subtraction to get the value.",
    ],
    pitfalls: [
      "Adding before multiplying and breaking order of operations.",
      "Substituting into only some of the variable's appearances.",
    ],
    vocabulary: [
      { term: "Substitute", definition: "Replace a variable with a specific number." },
    ],
    quickCheck: {
      prompt: "Evaluate $2x + 5$ when $x = 3$.",
      choices: ["$16$", "$11$", "$10$", "$13$"],
      correctIndex: 1,
      explanation: "$2(3) + 5 = 6 + 5 = 11$; multiply before adding.",
    },
  },
  "exponent-expressions": {
    topicId: "exponent-expressions",
    intro:
      "An exponent is a shorthand for repeated multiplication. $3^4$ means $3 \\times 3 \\times 3 \\times 3 = 81$. The base is the number being multiplied; the exponent tells how many times. In a larger expression, exponents are handled before multiplication and addition.",
    keyIdea:
      "An exponent counts how many times to multiply the base by itself. Evaluate exponents before multiplying or adding.",
    methodSteps: [
      "Identify the base and the exponent.",
      "Multiply the base by itself, exponent-many times.",
      "In a bigger expression, do exponents right after parentheses, before multiply/add.",
    ],
    pitfalls: [
      "Multiplying the base by the exponent (e.g. $3^4 = 12$) instead of repeated multiplication.",
      "Doing multiplication or addition before the exponent.",
    ],
    vocabulary: [
      { term: "Base", definition: "The number being multiplied repeatedly." },
      { term: "Exponent", definition: "How many times the base is used as a factor." },
    ],
    quickCheck: {
      prompt: "What is $2^3$?",
      choices: ["$6$", "$8$", "$9$", "$5$"],
      correctIndex: 1,
      explanation: "$2 \\times 2 \\times 2 = 8$.",
    },
  },
  "equivalent-expressions": {
    topicId: "equivalent-expressions",
    intro:
      "Two expressions are equivalent if they always have the same value. The distributive property and combining like terms turn one expression into an equal one that looks different. For example $3(x + 2) = 3x + 6$, and $2x + 5x = 7x$. Equivalent expressions are the same value in different clothes.",
    keyIdea:
      "The distributive property and combining like terms rewrite an expression without changing its value.",
    methodSteps: [
      "Distribute any number multiplied across parentheses: $a(b + c) = ab + ac$.",
      "Combine like terms - terms with the same variable part.",
      "Compare: equivalent expressions give the same result for every value.",
    ],
    pitfalls: [
      "Distributing to only the first term inside parentheses.",
      "Combining terms that are not alike, like $3x$ and $5$.",
    ],
    vocabulary: [
      { term: "Distributive property", definition: "$a(b + c) = ab + ac$; multiply the outside number by each inside term." },
      { term: "Like terms", definition: "Terms with the same variable part that can be combined." },
    ],
    quickCheck: {
      prompt: "Which is equivalent to $2(x + 4)$?",
      choices: ["$2x + 4$", "$2x + 8$", "$x + 8$", "$2x + 6$"],
      correctIndex: 1,
      explanation: "Distribute: $2 \\cdot x + 2 \\cdot 4 = 2x + 8$.",
    },
  },
  "write-equations": {
    topicId: "write-equations",
    intro:
      "A one-step equation captures a situation with a single operation on the variable. 'A number plus 7 is 12' becomes $x + 7 = 12$. Writing the equation is about translating words into a variable, an operation, and an equals sign.",
    keyIdea:
      "Pick a variable for the unknown, translate the operation, and use 'is' or 'equals' as the equals sign.",
    methodSteps: [
      "Choose a letter for the unknown quantity.",
      "Translate the described operation (add, subtract, times, divided by).",
      "Place the equals sign where the sentence says 'is' or 'equals.'",
    ],
    pitfalls: [
      "Reversing subtraction or division order.",
      "Writing an expression (no equals sign) when an equation was asked for.",
    ],
    quickCheck: {
      prompt: "Which equation means 'a number times 4 is 20'?",
      choices: ["$x + 4 = 20$", "$4x = 20$", "$x - 4 = 20$", "$\\tfrac{x}{4} = 20$"],
      correctIndex: 1,
      explanation: "'Times 4' is $4x$, and 'is' gives the equals sign.",
    },
  },
  "write-inequalities": {
    topicId: "write-inequalities",
    intro:
      "An inequality describes a range instead of a single value. 'At least 10' means 10 or more, written $x \\ge 10$. Key phrases map to symbols: 'at least' and 'no less than' are $\\ge$; 'at most' and 'no more than' are $\\le$; 'more than' is $>$; 'less than' is $<$.",
    keyIdea:
      "Match the phrase to the symbol. 'Or equal to' phrases (at least, at most) use $\\ge$ or $\\le$.",
    methodSteps: [
      "Choose a variable for the unknown.",
      "Translate the comparison phrase into a symbol.",
      "Write the variable, symbol, and value.",
    ],
    pitfalls: [
      "Mixing up 'at least' ($\\ge$) with 'more than' ($>$).",
      "Flipping the direction of the inequality.",
    ],
    quickCheck: {
      prompt: "'A number is at most 8' is written as...",
      choices: ["$x < 8$", "$x \\le 8$", "$x > 8$", "$x \\ge 8$"],
      correctIndex: 1,
      explanation: "'At most 8' means 8 or less, so $x \\le 8$.",
    },
  },
  "solve-equations": {
    topicId: "solve-equations",
    intro:
      "Solving a one-step equation means getting the variable alone using the inverse operation. If something is added, subtract it; if multiplied, divide. Whatever you do to one side, do to the other so the equation stays balanced. For $x + 5 = 12$, subtract 5 from both sides to get $x = 7$.",
    keyIdea:
      "Undo the operation on the variable with its inverse, doing the same thing to both sides.",
    methodSteps: [
      "See what is being done to the variable (add, subtract, multiply, or divide).",
      "Apply the inverse operation to both sides.",
      "Simplify to isolate the variable.",
      "Check by substituting your answer into the original equation.",
    ],
    pitfalls: [
      "Changing only one side of the equation.",
      "Using the same operation instead of the inverse (adding when you should subtract).",
    ],
    vocabulary: [
      { term: "Inverse operation", definition: "The operation that undoes another: add/subtract, multiply/divide." },
    ],
    quickCheck: {
      prompt: "Solve $x + 5 = 12$.",
      choices: ["$x = 17$", "$x = 7$", "$x = 60$", "$x = 5$"],
      correctIndex: 1,
      explanation: "Subtract 5 from both sides: $x = 12 - 5 = 7$.",
    },
  },
  "solve-inequalities": {
    topicId: "solve-inequalities",
    intro:
      "You solve a one-step inequality just like an equation, with one twist: if you multiply or divide both sides by a negative number, you must flip the inequality symbol. That is because multiplying by a negative reverses the order of the number line.",
    keyIdea:
      "Solve like an equation, but flip the symbol whenever you multiply or divide both sides by a negative.",
    methodSteps: [
      "Use the inverse operation to isolate the variable.",
      "If you multiply or divide by a negative, reverse the inequality symbol.",
      "Write the solution and graph it if asked - open circle for $<,>$ and closed for $\\le,\\ge$.",
    ],
    pitfalls: [
      "Forgetting to flip the symbol after dividing by a negative.",
      "Flipping when you only added or subtracted - that never flips it.",
    ],
    quickCheck: {
      prompt: "Solve $-2x < 6$.",
      choices: ["$x < -3$", "$x > -3$", "$x < 3$", "$x > 3$"],
      correctIndex: 1,
      explanation: "Divide by $-2$ and flip the symbol: $x > -3$.",
    },
  },
  "independent-dependent": {
    topicId: "independent-dependent",
    intro:
      "In a relationship between two quantities, the independent variable is the one you choose or control, and the dependent variable is the one that responds. If you buy $x$ tickets at $5 each and pay $y$ dollars, then $x$ is independent and $y$ depends on it: $y = 5x$.",
    keyIdea:
      "The independent variable is the input you control; the dependent variable is the output that changes because of it.",
    methodSteps: [
      "Identify which quantity is chosen or set freely - that is independent.",
      "Identify which quantity responds to the first - that is dependent.",
      "Write the rule with the dependent variable by itself, in terms of the independent one.",
    ],
    pitfalls: [
      "Swapping which variable depends on which.",
      "Assuming the alphabetically first letter is always independent.",
    ],
    vocabulary: [
      { term: "Independent variable", definition: "The input quantity you choose or control." },
      { term: "Dependent variable", definition: "The output quantity that responds to the input." },
    ],
    quickCheck: {
      prompt: "Total cost $y$ depends on number of tickets $x$. Which is independent?",
      choices: ["$y$, the cost", "$x$, the number of tickets", "Both", "Neither"],
      correctIndex: 1,
      explanation: "You choose how many tickets ($x$); the cost ($y$) responds, so $x$ is independent.",
    },
  },
  "ratios-rates": {
    topicId: "ratios-rates",
    intro:
      "A ratio compares two quantities, like 3 cups of flour to 2 cups of sugar, written $3:2$ or $\\tfrac{3}{2}$. A rate is a ratio comparing different units, like 60 miles in 2 hours. A unit rate simplifies a rate to 'per one,' like 30 miles per hour.",
    keyIdea:
      "A ratio compares two amounts; a unit rate expresses it 'per one' by dividing.",
    methodSteps: [
      "Write the two quantities as a ratio in the given order.",
      "For a rate, keep the units (miles per hour, dollars per pound).",
      "For a unit rate, divide so the second quantity becomes 1.",
    ],
    pitfalls: [
      "Reversing the order of the ratio.",
      "Forgetting the units on a rate.",
    ],
    vocabulary: [
      { term: "Ratio", definition: "A comparison of two quantities." },
      { term: "Unit rate", definition: "A rate with a denominator of 1, like miles per hour." },
    ],
    quickCheck: {
      prompt: "A car goes 150 miles in 3 hours. What is the unit rate?",
      choices: ["$150$ mph", "$50$ mph", "$3$ mph", "$450$ mph"],
      correctIndex: 1,
      explanation: "Divide $150 \\div 3 = 50$ miles per hour.",
    },
  },
  "convert-units": {
    topicId: "convert-units",
    intro:
      "Converting measurement units uses a conversion factor - a ratio equal to 1, like $\\tfrac{12 \\text{ in}}{1 \\text{ ft}}$. You multiply so the unit you are leaving cancels and the unit you want remains. Setting it up so units cancel keeps you from multiplying when you should divide.",
    keyIdea:
      "Multiply by a conversion factor arranged so the old unit cancels and the new unit is left.",
    methodSteps: [
      "Write the measurement you have.",
      "Multiply by a conversion factor (a ratio equal to 1) with the old unit on the bottom.",
      "Cancel the matching units and multiply the numbers.",
    ],
    pitfalls: [
      "Putting the conversion factor upside down so units do not cancel.",
      "Multiplying when the setup calls for dividing.",
    ],
    vocabulary: [
      { term: "Conversion factor", definition: "A ratio of equal measures, equal to 1, used to change units." },
    ],
    quickCheck: {
      prompt: "How many inches are in 3 feet?",
      choices: ["$15$", "$36$", "$4$", "$12$"],
      correctIndex: 1,
      explanation: "$3 \\text{ ft} \\times \\tfrac{12 \\text{ in}}{1 \\text{ ft}} = 36$ inches.",
    },
  },

  // --- Unit 3: Measurement, Geometry & Spatial Reasoning ----------------
  "measuring-angles": {
    topicId: "measuring-angles",
    intro:
      "Angles are measured in degrees, from $0\\degree$ to $360\\degree$. A right angle is $90\\degree$, a straight angle is $180\\degree$. You measure an angle with a protractor by lining up its center on the vertex and reading where the second ray crosses the scale.",
    keyIdea:
      "Angle size is measured in degrees. A right angle is $90\\degree$; a straight angle is $180\\degree$.",
    methodSteps: [
      "Place the protractor's center on the angle's vertex.",
      "Line up one ray with the zero line.",
      "Read the degree mark where the other ray crosses.",
    ],
    pitfalls: [
      "Reading the wrong protractor scale (inner vs outer).",
      "Not lining the vertex up with the protractor's center.",
    ],
    vocabulary: [
      { term: "Vertex", definition: "The point where the two rays of an angle meet." },
      { term: "Degree", definition: "The unit for measuring angles; a full turn is $360\\degree$." },
    ],
    quickCheck: {
      prompt: "How many degrees is a right angle?",
      choices: ["$45\\degree$", "$90\\degree$", "$180\\degree$", "$360\\degree$"],
      correctIndex: 1,
      explanation: "A right angle is a square corner, exactly $90\\degree$.",
    },
  },
  "angle-relationships": {
    topicId: "angle-relationships",
    intro:
      "Two angles are complementary if they add to $90\\degree$ and supplementary if they add to $180\\degree$. If you know one angle in such a pair, you can find the other by subtracting from $90$ or $180$. This turns an angle problem into a quick subtraction.",
    keyIdea:
      "Complementary angles add to $90\\degree$; supplementary angles add to $180\\degree$. Subtract to find the missing one.",
    methodSteps: [
      "Decide whether the pair is complementary ($90\\degree$) or supplementary ($180\\degree$).",
      "Subtract the known angle from $90$ or $180$.",
      "The result is the missing angle.",
    ],
    pitfalls: [
      "Mixing up complementary (90) and supplementary (180).",
      "Adding when you should subtract to find the missing angle.",
    ],
    vocabulary: [
      { term: "Complementary", definition: "Two angles that add to $90\\degree$." },
      { term: "Supplementary", definition: "Two angles that add to $180\\degree$." },
    ],
    quickCheck: {
      prompt: "An angle is supplementary to $130\\degree$. What is it?",
      choices: ["$50\\degree$", "$40\\degree$", "$230\\degree$", "$60\\degree$"],
      correctIndex: 0,
      explanation: "Supplementary angles add to $180\\degree$: $180 - 130 = 50\\degree$.",
    },
  },
  "area-2d": {
    topicId: "area-2d",
    intro:
      "Area is the number of unit squares that cover a flat shape. Each shape has a formula: a rectangle is base times height, a triangle is half of base times height, and a parallelogram is base times height. Picking the right formula and plugging in the measurements gives the area in square units.",
    keyIdea:
      "Area counts unit squares. Match the shape to its formula, then substitute the measurements.",
    methodSteps: [
      "Identify the shape and choose its area formula.",
      "Substitute the base and height (use the perpendicular height).",
      "Multiply and, for a triangle, take half.",
      "Label the answer in square units.",
    ],
    pitfalls: [
      "Forgetting the $\\tfrac{1}{2}$ for a triangle.",
      "Using a slanted side instead of the perpendicular height.",
    ],
    vocabulary: [
      { term: "Area", definition: "The amount of surface a shape covers, in square units." },
    ],
    quickCheck: {
      prompt: "Area of a triangle with base 6 and height 4?",
      choices: ["$24$", "$12$", "$10$", "$20$"],
      correctIndex: 1,
      explanation: "$\\tfrac{1}{2} \\times 6 \\times 4 = 12$ square units.",
    },
  },
  "area-composite": {
    topicId: "area-composite",
    intro:
      "A composite figure is made of simpler shapes joined together. To find its area, break it into rectangles and triangles, find each piece's area, and add them. Sometimes it is easier to find a big rectangle's area and subtract a missing corner.",
    keyIdea:
      "Break a composite shape into simple pieces, find each area, and add (or subtract a missing region).",
    methodSteps: [
      "Divide the figure into rectangles and triangles.",
      "Find the area of each piece with its formula.",
      "Add the pieces (or subtract a cut-out region).",
      "Label the total in square units.",
    ],
    pitfalls: [
      "Double-counting an overlapping region.",
      "Missing a hidden dimension you need to compute from the given ones.",
    ],
    quickCheck: {
      prompt: "A shape splits into a $4\\times5$ rectangle and a triangle of area 6. Total area?",
      choices: ["$20$", "$26$", "$14$", "$30$"],
      correctIndex: 1,
      explanation: "Rectangle $20$ plus triangle $6$ equals $26$ square units.",
    },
  },
  "nets-3d": {
    topicId: "nets-3d",
    intro:
      "A net is a flat pattern that folds up into a 3D solid. A cube's net is six squares; a rectangular prism's net is six rectangles in pairs. Nets help you see every face of a solid at once, which makes finding surface area easier.",
    keyIdea:
      "A net is the unfolded surface of a solid - every face laid flat. Fold it mentally to match it to the solid.",
    methodSteps: [
      "Identify the solid and count its faces.",
      "Match each face in the net to a face on the solid.",
      "Check that the net folds up without overlaps or gaps.",
    ],
    pitfalls: [
      "Choosing a net that would overlap when folded.",
      "Miscounting the number of faces.",
    ],
    vocabulary: [
      { term: "Net", definition: "A 2D pattern that folds into a 3D solid." },
      { term: "Face", definition: "A flat surface of a solid." },
    ],
    quickCheck: {
      prompt: "How many squares are in the net of a cube?",
      choices: ["$4$", "$6$", "$8$", "$12$"],
      correctIndex: 1,
      explanation: "A cube has 6 faces, so its net has 6 squares.",
    },
  },
  "surface-area": {
    topicId: "surface-area",
    intro:
      "Surface area is the total area of all the faces of a solid. For a rectangular prism, find the area of each of the six faces and add them - opposite faces match, so it is $2(lw + lh + wh)$. Thinking in terms of the net makes sure you count every face.",
    keyIdea:
      "Surface area is the sum of the areas of every face. Use the net so you do not miss one.",
    methodSteps: [
      "List every face of the solid (a net helps).",
      "Find the area of each face.",
      "Add all the face areas together.",
      "Label the total in square units.",
    ],
    pitfalls: [
      "Forgetting the back, bottom, or hidden faces.",
      "Confusing surface area (square units) with volume (cubic units).",
    ],
    quickCheck: {
      prompt: "Surface area of a cube with side 2?",
      choices: ["$8$", "$24$", "$12$", "$6$"],
      correctIndex: 1,
      explanation: "Each face is $2\\times2=4$, and there are 6 faces: $6 \\times 4 = 24$.",
    },
  },
  "volume-prisms": {
    topicId: "volume-prisms",
    intro:
      "Volume measures how much space a solid fills, in cubic units. For a rectangular prism, volume is length times width times height. You can also think of it as the area of the base times the height - stacking the base up to fill the solid.",
    keyIdea:
      "Volume of a prism is the base area times the height: $V = l \\times w \\times h$.",
    methodSteps: [
      "Find the length, width, and height (or the base area and height).",
      "Multiply them together.",
      "Label the answer in cubic units.",
    ],
    pitfalls: [
      "Adding dimensions instead of multiplying.",
      "Labeling volume in square units instead of cubic units.",
    ],
    vocabulary: [
      { term: "Volume", definition: "The space a solid fills, measured in cubic units." },
    ],
    quickCheck: {
      prompt: "Volume of a box $2 \\times 3 \\times 4$?",
      choices: ["$9$", "$24$", "$14$", "$12$"],
      correctIndex: 1,
      explanation: "$2 \\times 3 \\times 4 = 24$ cubic units.",
    },
  },
  "coordinate-plane": {
    topicId: "coordinate-plane",
    intro:
      "The coordinate plane has four quadrants formed by the x-axis and y-axis. A point is named by an ordered pair $(x, y)$: move right/left by $x$, then up/down by $y$. Signs tell you the quadrant - both positive is the top right, both negative is the bottom left.",
    keyIdea:
      "An ordered pair $(x, y)$ means over $x$, then up $y$. The signs place the point in one of four quadrants.",
    methodSteps: [
      "Start at the origin $(0, 0)$.",
      "Move along the x-axis by the first number (right if positive, left if negative).",
      "Move parallel to the y-axis by the second number (up if positive, down if negative).",
      "Mark the point.",
    ],
    pitfalls: [
      "Reversing the order - $x$ comes before $y$.",
      "Moving up first instead of across first.",
    ],
    vocabulary: [
      { term: "Ordered pair", definition: "$(x, y)$ giving horizontal then vertical position." },
      { term: "Quadrant", definition: "One of the four regions of the coordinate plane." },
    ],
    quickCheck: {
      prompt: "In which quadrant is $(-3, 5)$?",
      choices: ["Quadrant I", "Quadrant II", "Quadrant III", "Quadrant IV"],
      correctIndex: 1,
      explanation: "Negative $x$ and positive $y$ is the top-left region, Quadrant II.",
    },
  },
  "graphing-polygons": {
    topicId: "graphing-polygons",
    intro:
      "You can draw a polygon by plotting its vertices from coordinates and connecting them in order. Side lengths of horizontal or vertical sides are found by subtracting coordinates (or adding absolute values when a side crosses an axis). This links geometry to the coordinate plane.",
    keyIdea:
      "Plot each vertex, connect in order, and find horizontal/vertical side lengths by the difference of coordinates.",
    methodSteps: [
      "Plot each vertex from its ordered pair.",
      "Connect the points in the given order to form the polygon.",
      "For a horizontal side, subtract the x-coordinates; for a vertical side, subtract the y-coordinates.",
      "Use absolute value so the length is positive.",
    ],
    pitfalls: [
      "Connecting vertices in the wrong order and making a crossed shape.",
      "Getting a negative length by not using absolute value.",
    ],
    quickCheck: {
      prompt: "Length of the segment from $(2, 1)$ to $(2, 6)$?",
      choices: ["$4$", "$5$", "$8$", "$3$"],
      correctIndex: 1,
      explanation: "Same x, so subtract y-values: $|6 - 1| = 5$.",
    },
  },

  // --- Unit 4: Data, Probability & Statistical Reasoning ----------------
  "sample-size": {
    topicId: "sample-size",
    intro:
      "A sample is a smaller group chosen to stand in for a bigger group, called the population. The sample size is just how many are in that smaller group - if you ask 20 students their favorite lunch, the sample size is 20. A bigger, fairly chosen sample usually represents the whole group better than a tiny or lopsided one.",
    keyIdea:
      "A sample stands in for a population. Bigger, less biased samples tend to represent the population more reliably.",
    methodSteps: [
      "Identify the population you want to learn about.",
      "Consider whether the sample is large enough and chosen fairly.",
      "Judge whether the sample can reasonably represent the whole population.",
    ],
    pitfalls: [
      "Trusting a tiny or clearly biased sample.",
      "Confusing the sample with the whole population.",
    ],
    vocabulary: [
      { term: "Population", definition: "The entire group you want to study." },
      { term: "Sample", definition: "A part of the population chosen to represent it." },
    ],
    quickCheck: {
      prompt: "Which sample best represents all students in a school?",
      choices: [
        "The first 3 students you see",
        "A random group from every grade",
        "Only your friends",
        "Only the basketball team",
      ],
      correctIndex: 1,
      explanation: "A random group spread across grades is less biased and more representative.",
    },
  },
  "measures-center-spread": {
    topicId: "measures-center-spread",
    intro:
      "Data can be summarized by center and spread. The median is the middle value, the mode is the most frequent, the range is the largest minus the smallest, and the interquartile range (IQR) measures the spread of the middle half. Each number tells a different part of the data's story.",
    keyIdea:
      "Median and mode describe the center; range and IQR describe how spread out the data is.",
    methodSteps: [
      "Order the data from least to greatest.",
      "Median: the middle value (average the two middle ones if the count is even).",
      "Mode: the value that appears most often.",
      "Range: largest minus smallest. IQR: upper quartile minus lower quartile.",
    ],
    pitfalls: [
      "Forgetting to order the data before finding the median.",
      "Confusing range (a spread) with mode (a center).",
    ],
    vocabulary: [
      { term: "Median", definition: "The middle value of ordered data." },
      { term: "Range", definition: "The largest value minus the smallest." },
      { term: "IQR", definition: "The interquartile range: the spread of the middle half of the data." },
    ],
    quickCheck: {
      prompt: "Median of $3, 7, 5, 9, 1$?",
      choices: ["$5$", "$7$", "$3$", "$9$"],
      correctIndex: 0,
      explanation: "Ordered: $1,3,5,7,9$; the middle value is $5$.",
    },
  },
  "box-plots": {
    topicId: "box-plots",
    intro:
      "A box plot shows a five-number summary: minimum, lower quartile, median, upper quartile, and maximum. The box spans the middle half of the data and the whiskers reach the extremes. Reading a box plot tells you the center and the spread at a glance.",
    keyIdea:
      "A box plot displays the five-number summary. The box is the middle half; the line inside is the median.",
    methodSteps: [
      "Order the data and find the minimum, median, and maximum.",
      "Find the lower and upper quartiles (medians of each half).",
      "Draw a box from lower to upper quartile with a line at the median, and whiskers to the min and max.",
    ],
    pitfalls: [
      "Confusing the median line with the mean.",
      "Reading the box edges as min/max instead of the quartiles.",
    ],
    vocabulary: [
      { term: "Quartile", definition: "A value dividing ordered data into quarters." },
      { term: "Five-number summary", definition: "Minimum, Q1, median, Q3, and maximum." },
    ],
    quickCheck: {
      prompt: "In a box plot, what does the line inside the box show?",
      choices: ["The mean", "The median", "The range", "The maximum"],
      correctIndex: 1,
      explanation: "The line inside the box marks the median of the data.",
    },
  },
  "data-shape": {
    topicId: "data-shape",
    intro:
      "The shape of a data set describes its overall pattern. Symmetric data balances around the center; skewed data has a longer tail on one side; outliers are values far from the rest. Shape helps you decide which measure of center best summarizes the data.",
    keyIdea:
      "Describe data by its shape: symmetric, skewed left/right, or containing outliers. Shape guides which center to trust.",
    methodSteps: [
      "Look at how the data clusters and where the tails are.",
      "Symmetric: balanced around the middle. Skewed: a long tail on one side.",
      "Spot outliers - points far from the rest.",
      "Choose median for skewed data, since the mean gets pulled by outliers.",
    ],
    pitfalls: [
      "Calling data symmetric when it has a long one-sided tail.",
      "Using the mean when a strong outlier distorts it.",
    ],
    vocabulary: [
      { term: "Skewed", definition: "Data with a longer tail on one side." },
      { term: "Outlier", definition: "A value far away from the rest of the data." },
    ],
    quickCheck: {
      prompt: "Data with a long tail stretching to the right is...",
      choices: ["Symmetric", "Skewed right", "Skewed left", "Uniform"],
      correctIndex: 1,
      explanation: "A tail extending toward the larger values means the data is skewed right.",
    },
  },
  likelihood: {
    topicId: "likelihood",
    intro:
      "Likelihood describes how probable an event is, on a scale from impossible to certain. A probability of 0 means it cannot happen, 1 means it is certain, and $\\tfrac{1}{2}$ means equally likely as not. Words like 'unlikely' and 'likely' map onto this 0-to-1 scale.",
    keyIdea:
      "Probability runs from 0 (impossible) to 1 (certain), with $\\tfrac{1}{2}$ meaning as likely as not.",
    methodSteps: [
      "Decide if the event is impossible, unlikely, even, likely, or certain.",
      "Match it to a value between 0 and 1.",
      "Closer to 1 means more likely; closer to 0 means less likely.",
    ],
    pitfalls: [
      "Giving a probability greater than 1 or less than 0.",
      "Treating 'unlikely' as impossible.",
    ],
    quickCheck: {
      prompt: "A probability of 0 means the event is...",
      choices: ["Certain", "Impossible", "Likely", "Even"],
      correctIndex: 1,
      explanation: "A probability of 0 means the event cannot happen.",
    },
  },
  "simple-probability": {
    topicId: "simple-probability",
    intro:
      "The probability of a simple event is the number of favorable outcomes divided by the total number of equally likely outcomes. Rolling a 4 on a die is $\\tfrac{1}{6}$: one favorable outcome out of six. Writing it as a fraction (then maybe a decimal or percent) gives the exact chance.",
    keyIdea:
      "Probability = favorable outcomes over total equally likely outcomes.",
    methodSteps: [
      "Count the favorable outcomes.",
      "Count the total number of equally likely outcomes.",
      "Write the probability as favorable over total, then reduce.",
    ],
    pitfalls: [
      "Counting outcomes that are not equally likely.",
      "Putting total on top instead of favorable.",
    ],
    vocabulary: [
      { term: "Outcome", definition: "A possible result of an experiment." },
      { term: "Favorable outcome", definition: "An outcome that counts as the event happening." },
    ],
    quickCheck: {
      prompt: "Probability of rolling an even number on a 6-sided die?",
      choices: ["$\\tfrac{1}{6}$", "$\\tfrac{1}{2}$", "$\\tfrac{1}{3}$", "$\\tfrac{2}{3}$"],
      correctIndex: 1,
      explanation: "Evens are 2, 4, 6: three out of six, so $\\tfrac{3}{6} = \\tfrac{1}{2}$.",
    },
  },
  "complement-probability": {
    topicId: "complement-probability",
    intro:
      "The complement of an event is everything except that event. Because something either happens or it does not, the probability of an event plus the probability of its complement equals 1. So if the chance of rain is $\\tfrac{1}{4}$, the chance of no rain is $1 - \\tfrac{1}{4} = \\tfrac{3}{4}$.",
    keyIdea:
      "An event and its complement add to 1. Subtract from 1 to find the complement's probability.",
    methodSteps: [
      "Find the probability of the event.",
      "Subtract it from 1 to get the complement's probability.",
      "Check that the two probabilities add to 1.",
    ],
    pitfalls: [
      "Adding instead of subtracting from 1.",
      "Forgetting that the two probabilities must total 1.",
    ],
    vocabulary: [
      { term: "Complement", definition: "All outcomes where the event does not happen." },
    ],
    quickCheck: {
      prompt: "If $P(\\text{win}) = \\tfrac{2}{5}$, what is $P(\\text{not win})$?",
      choices: ["$\\tfrac{2}{5}$", "$\\tfrac{3}{5}$", "$\\tfrac{5}{2}$", "$1$"],
      correctIndex: 1,
      explanation: "The complement is $1 - \\tfrac{2}{5} = \\tfrac{3}{5}$.",
    },
  },
};

export function getLesson(topicId: TopicId): Lesson {
  return LESSONS[topicId];
}
